import { IssueStatusType } from '@kcftech/prometheus-client';
import { InfiniteScroll } from 'components/infinite_scroll/InfiniteScroll';
import { SpinnerArea } from 'components/spinner/SpinnerArea';
import { REFRESH_INTERVAL } from 'configs/constants';
import { useInfiniteMachineHealthIssuesDataSource } from 'custom_hooks/data_hooks/data_source/machineHealthIssuesDataSource';
import { createDraftQueueParams } from 'pages/Desk/issues/issue_details/useDraftIssueQueue';
import { IssuesDefaultSort } from 'pages/Desk/issues/shared';
import { DraftIssueItem } from 'pages/Location/alerts_and_drafts/draft_issues/draft_issue_item/DraftIssueItem';
import {
    DraftIssuesContent,
    EmptyState,
    IssueCardList,
} from 'pages/Location/alerts_and_drafts/draft_issues/styled';
import { getThirtyDaysAgo } from 'pages/Location/alerts_and_drafts/utils';
import { forwardRef, memo, useImperativeHandle } from 'react';
import { getIssueDetailsRoute } from 'utils/RouteHelper';

const PAGE_LIMIT = 10;

export type DraftIssuesWidgetRef = {
    refetch: () => Promise<unknown>;
};

type DraftIssuesWidgetProps = {
    locationId: string;
};

function DraftIssuesWidget(
    { locationId }: DraftIssuesWidgetProps,
    ref: React.ForwardedRef<DraftIssuesWidgetRef>,
) {
    const {
        results,
        filter,
        sort,
        isLoading,
        hasNext,
        loadNext,
        refetch,
    } = useInfiniteMachineHealthIssuesDataSource({
        name: 'sentry-draft-issues',
        defaultFilter: {
            locationId,
            statusType: [IssueStatusType.Draft],
            created: getThirtyDaysAgo(),
        },
        pageLimit: PAGE_LIMIT,
        enabled: true,
        keepPreviousData: true,
        defaultSort: IssuesDefaultSort,
        refetchInterval: REFRESH_INTERVAL,
        staleTime: REFRESH_INTERVAL,
    });

    useImperativeHandle(
        ref,
        () => ({
            refetch,
        }),
        [refetch],
    );

    if (!isLoading && !results?.length) {
        return <EmptyState>No draft issues found</EmptyState>;
    }

    const { created, ...filterWithoutTime } = filter;
    const queueParams = createDraftQueueParams('machineHealth', filterWithoutTime, sort);

    return (
        <SpinnerArea isLoading={isLoading}>
            <DraftIssuesContent>
                <InfiniteScroll
                    onLoadMore={loadNext}
                    enabled={!!hasNext}
                    isLoading={isLoading && !results}
                    aria-label="Draft issues list"
                    infiniteScrollDirection="bottom"
                >
                    <IssueCardList>
                        {results?.map((issue) => (
                            <DraftIssueItem
                                key={issue.Id}
                                issue={issue}
                                to={`${getIssueDetailsRoute(
                                    locationId,
                                    issue.Id,
                                )}${queueParams}`}
                            />
                        ))}
                    </IssueCardList>
                </InfiniteScroll>
            </DraftIssuesContent>
        </SpinnerArea>
    );
}

const memoDraftIssuesWidget = memo(
    forwardRef<DraftIssuesWidgetRef, DraftIssuesWidgetProps>(DraftIssuesWidget),
);

export { memoDraftIssuesWidget as DraftIssuesWidget };
